import React from 'react';
import { Helmet } from 'react-helmet-async';

interface StructuredDataProps {
  type?: 'service' | 'organization' | 'faq' | 'breadcrumb' | 'review' | 'appSecurity' | 'devSecOps' | 'penTesting' | 'cloudSecurity' | 'advisory';
  data?: {
    breadcrumbs?: Array<{ name: string; url: string }>;
  };
}

const StructuredData: React.FC<StructuredDataProps> = ({ type = 'organization', data }) => {
  
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'ProfessionalService'],
    name: 'CIPHER Security',
    alternateName: 'CIPHERSecurity.io',
    url: 'https://ciphersecurity.io',
    logo: {
      '@type': 'ImageObject',
      url: 'https://ciphersecurity.io/images/cipher_character_notext.png',
      width: '512',
      height: '512'
    },
    image: [
      'https://ciphersecurity.io/images/cipher_character_notext.png',
      'https://ciphersecurity.io/images/cyber1.jpg'
    ],
    description: 'CIPHER Security provides comprehensive Cybersecurity Services including Penetration Testing, DevSecOps, Application Security, Cloud Security, and Compliance solutions for enterprises worldwide.',
    slogan: 'Prevent • Detect • Respond',
    foundingDate: '2024',
    industry: 'Cybersecurity Services',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: '10-50'
    },
    knowsAbout: [
      'Cybersecurity',
      'Penetration Testing',
      'DevSecOps',
      'Application Security',
      'Cloud Security',
      'Vulnerability Assessment',
      'Incident Response',
      'Compliance Auditing',
      'Threat Intelligence',
      'Security Training',
      'Risk Management',
      'SAST',
      'DAST',
      'SCA'
    ],
    serviceArea: {
      '@type': 'Place',
      name: 'Global'
    },
    areaServed: [
      'United States',
      'Canada', 
      'United Kingdom',
      'Australia',
      'Europe'
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@ciphersecurity.io',
        telephone: '+1-848-339-1572',
        availableLanguage: 'English',
        contactOption: 'TollFree'
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'sales@ciphersecurity.io',
        telephone: '+1-848-339-1572',
        availableLanguage: 'English'
      }
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Global',
      addressCountry: 'US'
    },
    sameAs: [
      'https://www.linkedin.com/company/ciphersecurity-io',
      'https://twitter.com/ciphersecurityio'
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Cybersecurity Services',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Application Security',
          description: 'Comprehensive security testing and code analysis to protect your applications from vulnerabilities. From SAST to DAST, we ensure your code is secure.',
          category: 'Cybersecurity Services',
          availability: 'InStock',
          businessFunction: 'http://purl.org/goodrelations/v1#Sell',
          itemOffered: {
            '@type': 'Service',
            name: 'Application Security Testing',
            serviceType: 'Security Testing',
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'Static Code Analysis', value: 'SAST' },
              { '@type': 'PropertyValue', name: 'Dynamic Testing', value: 'DAST' },
              { '@type': 'PropertyValue', name: 'Dependency Scanning', value: 'SCA' },
              { '@type': 'PropertyValue', name: 'Security Code Review', value: 'Manual Testing' }
            ]
          }
        },
        {
          '@type': 'Offer',
          name: 'DevSecOps',
          description: 'Embed security into your CI/CD pipeline. Incorporate prevention in Shift left with automated security testing that doesn\'t slow down your development velocity.',
          category: 'Cybersecurity Services',
          availability: 'InStock',
          businessFunction: 'http://purl.org/goodrelations/v1#Sell',
          itemOffered: {
            '@type': 'Service',
            name: 'DevSecOps Consulting',
            serviceType: 'Security Consulting',
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'CI/CD Security', value: 'Pipeline Integration' },
              { '@type': 'PropertyValue', name: 'Security Automation', value: 'Automated Testing' },
              { '@type': 'PropertyValue', name: 'Compliance as Code', value: 'Policy Automation' }
            ]
          }
        },
        {
          '@type': 'Offer',
          name: 'Offensive Security',
          description: 'Elite offensive security testing to identify vulnerabilities before attackers do. Web, mobile, network, and cloud security testing.',
          category: 'Cybersecurity Services',
          availability: 'InStock',
          businessFunction: 'http://purl.org/goodrelations/v1#Sell',
          itemOffered: {
            '@type': 'Service',
            name: 'Penetration Testing',
            serviceType: 'Security Assessment',
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'Web App Testing', value: 'Application Penetration Testing' },
              { '@type': 'PropertyValue', name: 'Network Testing', value: 'Infrastructure Testing' },
              { '@type': 'PropertyValue', name: 'Cloud Security Testing', value: 'Cloud Penetration Testing' },
              { '@type': 'PropertyValue', name: 'Red Team Operations', value: 'Advanced Persistent Threat Simulation' }
            ]
          }
        },
        {
          '@type': 'Offer',
          name: 'Cloud Security',
          description: 'Comprehensive cloud security services including architecture review, penetration testing, compliance readiness, and DevSecOps integration across multi-cloud environments.',
          category: 'Cybersecurity Services',
          availability: 'InStock',
          businessFunction: 'http://purl.org/goodrelations/v1#Sell',
          itemOffered: {
            '@type': 'Service',
            name: 'Cloud Security Assessment',
            serviceType: 'Security Assessment',
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'Architecture Review', value: 'Cloud Architecture Assessment' },
              { '@type': 'PropertyValue', name: 'Cloud Penetration Testing', value: 'Cloud Infrastructure Testing' },
              { '@type': 'PropertyValue', name: 'Compliance Readiness', value: 'Cloud Compliance Auditing' },
              { '@type': 'PropertyValue', name: 'DevSecOps Integration', value: 'Cloud Security Automation' }
            ]
          }
        },
        {
          '@type': 'Offer',
          name: 'Security Advisory Services',
          description: 'Strategic security advisory services to build mature security programs with expert guidance, governance frameworks, and comprehensive risk management.',
          category: 'Cybersecurity Services',
          availability: 'InStock',
          businessFunction: 'http://purl.org/goodrelations/v1#Sell',
          itemOffered: {
            '@type': 'Service',
            name: 'Security Advisory & Consulting',
            serviceType: 'Security Consulting',
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'Risk Assessment', value: 'Comprehensive Risk Analysis' },
              { '@type': 'PropertyValue', name: 'Vulnerability Management', value: 'Vulnerability Program Design' },
              { '@type': 'PropertyValue', name: 'Threat Intelligence', value: 'Strategic Threat Analysis' },
              { '@type': 'PropertyValue', name: 'Governance Risk & Compliance', value: 'GRC Program Implementation' }
            ]
          }
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '127',
      bestRating: '5',
      worstRating: '1'
    },
    review: [
      {
        '@type': 'Review',
        author: {
          '@type': 'Organization',
          name: 'Fortune 500 Financial Services Company'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        reviewBody: 'CIPHER Security delivered exceptional results in our application security assessment. Their developer-centric approach and actionable findings helped us significantly improve our security posture.',
        datePublished: '2024-12-01'
      },
      {
        '@type': 'Review',
        author: {
          '@type': 'Person',
          name: 'Enterprise CISO'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: '5',
          bestRating: '5'
        },
        reviewBody: 'Outstanding penetration testing services. The team identified critical vulnerabilities that other firms missed. Highly recommend for enterprise security assessments.',
        datePublished: '2024-11-15'
      }
    ]
  };

  const cybersecurityServiceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Cybersecurity Services',
    description: 'CIPHER Security provides comprehensive Cybersecurity Services including Penetration Testing, DevSecOps, Application Security, Cloud Security, and Compliance solutions for enterprises worldwide.',
    provider: {
      '@type': 'Organization',
      name: 'CIPHER Security',
      url: 'https://ciphersecurity.io'
    },
    serviceType: [
      'Penetration Testing',
      'DevSecOps Consulting', 
      'Application Security',
      'Cloud Security',
      'Vulnerability Assessment',
      'Security Compliance',
      'Incident Response',
      'Security Training'
    ],
    areaServed: {
      '@type': 'Place',
      name: 'Global'
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Cybersecurity Services Portfolio',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Penetration Testing',
          description: 'Professional penetration testing services for web applications, networks, and cloud infrastructure',
          priceRange: '$$$$',
          availability: 'InStock'
        },
        {
          '@type': 'Offer', 
          name: 'DevSecOps Implementation',
          description: 'Complete DevSecOps pipeline integration and security automation',
          priceRange: '$$$$',
          availability: 'InStock'
        }
      ]
    }
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What cybersecurity services does CIPHER Security provide?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'CIPHER Security provides comprehensive cybersecurity services including penetration testing, DevSecOps consulting, application security testing (SAST/DAST/SCA), cloud security assessments, vulnerability management, compliance auditing, incident response, and security training. Our services help organizations achieve 85% vulnerability reduction and 95% faster secure deployments.'
        }
      },
      {
        '@type': 'Question',
        name: 'How much does penetration testing cost in 2025?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Penetration testing costs range from $15,000-$75,000 depending on scope, with web application tests starting at $15,000, network assessments at $25,000, and comprehensive enterprise testing at $50,000+. We provide detailed quotes based on your specific infrastructure and compliance requirements.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is DevSecOps implementation and how long does it take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'DevSecOps implementation integrates security testing directly into CI/CD pipelines, enabling automated vulnerability scanning, policy enforcement, and compliance checks. Our typical DevSecOps implementation takes 4-8 weeks and results in 95% faster secure deployments while maintaining security standards.'
        }
      },
      {
        '@type': 'Question',
        name: 'Which industries does CIPHER Security specialize in?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We specialize in heavily regulated industries including financial services (PCI DSS), healthcare (HIPAA), government (FedRAMP), and technology companies requiring SOC 2 compliance. Our industry-specific expertise ensures tailored security solutions that meet regulatory requirements.'
        }
      },
      {
        '@type': 'Question',
        name: 'What is the difference between SAST, DAST, and SCA testing?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'SAST (Static Application Security Testing) analyzes source code for vulnerabilities, DAST (Dynamic Application Security Testing) tests running applications, and SCA (Software Composition Analysis) scans third-party dependencies. CIPHER Security implements all three as part of our comprehensive Application Security program.'
        }
      },
      {
        '@type': 'Question',
        name: 'How long does a cloud security assessment take?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Cloud security assessments typically take 2-4 weeks depending on the number of cloud environments and services. Our assessments cover AWS, Azure, and GCP, including CSPM (Cloud Security Posture Management), container security, and IAM configuration reviews, achieving 99.9% compliance rates.'
        }
      },
      {
        '@type': 'Question',
        name: 'What certifications do CIPHER Security consultants hold?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Our team holds industry-leading certifications including CISSP, CISM, OSCP, CEH, AWS Security Specialty, Azure Security Engineer, and SANS GIAC certifications. We maintain over 15 active certifications across our consulting team to ensure expertise in the latest security technologies.'
        }
      }
    ]
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'CIPHER Security',
    alternateName: 'CIPHERSecurity.io',
    url: 'https://ciphersecurity.io',
    description: 'CIPHER Security provides comprehensive Cybersecurity Services including Penetration Testing, DevSecOps, Application Security, Cloud Security, and Compliance solutions for enterprises worldwide.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://ciphersecurity.io/search?q={search_term_string}'
      },
      'query-input': 'required name=search_term_string'
    },
    publisher: {
      '@type': 'Organization',
      name: 'CIPHER Security'
    }
  };

  const breadcrumbSchema = data?.breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: data.breadcrumbs.map((crumb, index: number) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  } : null;

  // Service-specific schemas with pricing and timeline information
  const applicationSecuritySchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Application Security Testing Services',
    description: 'Comprehensive application security testing including SAST, DAST, SCA, and secure code review with 85% vulnerability reduction guarantee.',
    provider: {
      '@type': 'Organization',
      name: 'CIPHER Security',
      url: 'https://ciphersecurity.io'
    },
    serviceType: 'Application Security Testing',
    areaServed: 'Global',
    offers: {
      '@type': 'Offer',
      name: 'Application Security Testing Package',
      description: 'Complete application security assessment with SAST, DAST, SCA testing and expert remediation guidance',
      priceRange: '$15,000 - $50,000',
      priceCurrency: 'USD',
      availability: 'InStock',
      validThrough: '2025-12-31',
      category: 'Cybersecurity Services',
      deliveryLeadTime: {
        '@type': 'QuantitativeValue',
        value: '4-8',
        unitText: 'weeks'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Application Security Services',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'SAST Testing',
          description: 'Static Application Security Testing with source code analysis',
          priceRange: '$8,000 - $25,000'
        },
        {
          '@type': 'Offer', 
          name: 'DAST Testing',
          description: 'Dynamic Application Security Testing with runtime vulnerability assessment',
          priceRange: '$10,000 - $30,000'
        },
        {
          '@type': 'Offer',
          name: 'SCA Testing',
          description: 'Software Composition Analysis with dependency vulnerability scanning',
          priceRange: '$5,000 - $15,000'
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '127',
      bestRating: '5'
    }
  };

  const devSecOpsSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'DevSecOps Implementation Services',
    description: 'Expert DevSecOps consulting and CI/CD pipeline security integration with 95% faster secure deployments and automated security testing.',
    provider: {
      '@type': 'Organization',
      name: 'CIPHER Security',
      url: 'https://ciphersecurity.io'
    },
    serviceType: 'DevSecOps Consulting',
    areaServed: 'Global',
    offers: {
      '@type': 'Offer',
      name: 'DevSecOps Transformation Package',
      description: 'Complete DevSecOps implementation with CI/CD security integration, automation, and governance',
      priceRange: '$25,000 - $100,000',
      priceCurrency: 'USD',
      availability: 'InStock',
      validThrough: '2025-12-31',
      category: 'DevSecOps Services',
      deliveryLeadTime: {
        '@type': 'QuantitativeValue',
        value: '4-12',
        unitText: 'weeks'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'DevSecOps Services',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Pipeline Security Integration',
          description: 'CI/CD security gates and automated security testing integration',
          priceRange: '$15,000 - $40,000'
        },
        {
          '@type': 'Offer',
          name: 'Security Automation',
          description: 'Automated vulnerability management and security tool orchestration',
          priceRange: '$20,000 - $60,000'
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '89',
      bestRating: '5'
    }
  };

  const penetrationTestingSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Penetration Testing Services',
    description: 'Professional penetration testing services for web applications, networks, and cloud infrastructure with real-world attack simulation.',
    provider: {
      '@type': 'Organization',
      name: 'CIPHER Security',
      url: 'https://ciphersecurity.io'
    },
    serviceType: 'Penetration Testing',
    areaServed: 'Global',
    offers: {
      '@type': 'Offer',
      name: 'Penetration Testing Package',
      description: 'Comprehensive penetration testing with detailed reporting and remediation guidance',
      priceRange: '$15,000 - $75,000',
      priceCurrency: 'USD',
      availability: 'InStock',
      validThrough: '2025-12-31',
      category: 'Penetration Testing',
      deliveryLeadTime: {
        '@type': 'QuantitativeValue',
        value: '2-6',
        unitText: 'weeks'
      }
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Penetration Testing Services',
      itemListElement: [
        {
          '@type': 'Offer',
          name: 'Web Application Penetration Testing',
          description: 'Comprehensive web application security testing and vulnerability assessment',
          priceRange: '$15,000 - $35,000'
        },
        {
          '@type': 'Offer',
          name: 'Network Penetration Testing',
          description: 'Infrastructure and network security assessment with privilege escalation testing',
          priceRange: '$25,000 - $50,000'
        },
        {
          '@type': 'Offer',
          name: 'Cloud Penetration Testing',
          description: 'Cloud infrastructure security testing across AWS, Azure, and GCP',
          priceRange: '$30,000 - $75,000'
        }
      ]
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      reviewCount: '156',
      bestRating: '5'
    }
  };

  const getSchema = () => {
    switch (type) {
      case 'service':
        return cybersecurityServiceSchema;
      case 'appSecurity':
        return applicationSecuritySchema;
      case 'devSecOps':
        return devSecOpsSchema;
      case 'penTesting':
        return penetrationTestingSchema;
      case 'faq':
        return faqSchema;
      case 'breadcrumb':
        return breadcrumbSchema;
      case 'organization':
      default:
        return [organizationSchema, websiteSchema, faqSchema];
    }
  };

  const schema = getSchema();

  return (
    <Helmet>
      {Array.isArray(schema) ? (
        schema.map((schemaItem, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schemaItem)}
          </script>
        ))
      ) : (
        schema && (
          <script type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        )
      )}
    </Helmet>
  );
};

export default StructuredData;